import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './SignIn.css';

const SignIn = () => {
  const { t, i18n } = useTranslation();
  const { signin, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const topBarRef = useRef(null);

  // Language toggle handler
  const handleLanguageToggle = (lang) => {
    i18n.changeLanguage(lang);
  };

  // Load saved email from localStorage on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('remembered_email');
    const savedRememberMe = localStorage.getItem('remember_me') === 'true';
    
    if (savedEmail && savedRememberMe) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  // Scroll handler for top bar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    const result = await signin(email, password);
    
    if (result.success) {
      // Handle remember me functionality
      if (rememberMe) {
        // Save email and remember me preference
        localStorage.setItem('remember_me', 'true');
        localStorage.setItem('remembered_email', email);
      } else {
        // Clear saved data if remember me is unchecked
        localStorage.removeItem('remember_me');
        localStorage.removeItem('remembered_email');
      }
      // Redirect to account page
      navigate('/account');
    } else {
      setError(result.error || 'Sign in failed');
    }
    
    setIsLoading(false);
  };

  // Password reset handler
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetMessage('');
    setIsResettingPassword(true);
    
    if (!resetEmail) {
      setResetMessage('Please enter your email address');
      setIsResettingPassword(false);
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(resetEmail)) {
      setResetMessage('Please enter a valid email address');
      setIsResettingPassword(false);
      return;
    }
    
    console.log('[Password Reset] Starting password reset for:', resetEmail);
    
    try {
      console.log('[Password Reset] Calling resetPassword function...');
      const result = await resetPassword(resetEmail);
      console.log('[Password Reset] Result received:', result);
      
      if (result.success) {
        setResetMessage(result.message || 'Password reset email sent! Please check your inbox and spam folder.');
        // Clear the email field
        setResetEmail('');
        // Keep the form open for a few seconds so user can see the message
        setTimeout(() => {
          setShowForgotPassword(false);
          setResetMessage('');
        }, 8000);
      } else {
        // Show detailed error message
        const errorMsg = result.error || 'Failed to send reset email. Please try again.';
        setResetMessage(errorMsg);
        console.error('[Password Reset] Error:', result.errorCode, result.error);
      }
    } catch (error) {
      console.error('[Password Reset] Unexpected error:', error);
      setResetMessage('An unexpected error occurred. Please check the console for details and try again.');
    } finally {
      setIsResettingPassword(false);
    }
  };

  // Get current year for footer
  const currentYear = new Date().getFullYear();

  return (
    <div className="signin-wrapper">
      {/* Fixed Top Bar */}
      <nav 
        ref={topBarRef}
        className={`fixed-top-bar ${isScrolled ? 'scrolled' : ''}`}
      >
        <div className="top-bar-logo">
          <button
            className="language-toggle"
            aria-label={t('signin.topBar.toggleLanguage')}
            onClick={(e) => {
              const option = e.target.closest('.lang-option');
              if (option) {
                const targetLang = option.dataset.lang;
                if (targetLang) {
                  handleLanguageToggle(targetLang);
                }
              }
            }}
          >
            <span 
              className={`lang-option ${i18n.language === 'en' ? 'active' : ''}`} 
              data-lang="en"
            >
              EN
            </span>
            <span className="lang-separator">|</span>
            <span 
              className={`lang-option ${i18n.language === 'es' ? 'active' : ''}`} 
              data-lang="es"
            >
              ES
            </span>
          </button>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="86.609"
            height="77.109"
            viewBox="0 0 86.609 77.109"
          >
            <defs>
              <clipPath id={`clip-path-auth-${i18n.language}`}>
                <rect
                  width="42.305"
                  height="48.895"
                  transform="translate(-3 -6)"
                  fill="none"
                  stroke="#49a9ff"
                  strokeLinejoin="round"
                  strokeWidth="2"
                />
              </clipPath>
            </defs>
            <path
              d="M31.061,4.859,14.374,37.091.435,65.389c-1.175,2.385.114,4.365,2.866,4.4l27.76.34,27.76-.34c2.752-.034,4.041-2.013,2.866-4.4l-13.94-28.3Z"
              transform="translate(0 6.982)"
              fill="#49a9ff"
            />
            <g transform="translate(48.422 6)" style={{ isolation: 'isolate' }}>
              <g transform="translate(-1.119 0)">
                <g clipPath={`url(#clip-path-auth-${i18n.language})`}>
                  <path
                    d="M45.006,46.045,54.8,27.824l8.179-15.995a1.754,1.754,0,0,0-1.683-2.488L45.006,9.15l-16.289.191a1.754,1.754,0,0,0-1.681,2.488l8.179,15.995Z"
                    transform="translate(-26.854 -9.149)"
                    fill="none"
                    stroke="#49a9ff"
                    strokeLinejoin="round"
                    strokeWidth="2"
                  />
                </g>
              </g>
            </g>
          </svg>
        </div>
        <div className="top-bar-buttons">
          <Link 
            to={i18n.language === 'es' ? '/landing' : '/landing'} 
            className="top-bar-button tertiary"
          >
            {t('signin.topBar.overview')}
          </Link>
          <Link 
            to={i18n.language === 'es' ? '/subscriptions' : '/subscriptions'} 
            className="top-bar-button secondary"
          >
            {t('signin.topBar.plans')}
          </Link>
          <Link 
            to={i18n.language === 'es' ? '/signup' : '/signup'} 
            className="top-bar-button primary"
          >
            {t('signin.topBar.createAccount')}
          </Link>
        </div>
      </nav>

      {/* Auth Section */}
      <section className="auth-section">
        <article className="auth-card">
          <div className="auth-info">
            <img 
              src="/images/logos/logo.svg" 
              alt={t('signin.authInfo.logoAlt')} 
              className="auth-info-logo" 
            />
            <p 
              className="eyebrow" 
              style={{
                letterSpacing: '0.4em',
                textTransform: 'uppercase',
                color: 'rgba(244, 247, 251, 0.7)',
                margin: 0
              }}
            >
              {t('signin.authInfo.eyebrow')}
            </p>
            <h1>
              {t('signin.authInfo.heading.prefix')}{' '}
              <span>{t('signin.authInfo.heading.company')}</span>
            </h1>
            <p>{t('signin.authInfo.description')}</p>
            <ul className="auth-highlights">
              {t('signin.authInfo.highlights', { returnObjects: true }).map((highlight, index) => (
                <li key={index}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  {highlight}
                </li>
              ))}
            </ul>
          </div>
          <div className="auth-form">
            <div>
              <h2 className="form-title">{t('signin.form.title')}</h2>
              <p className="form-subtitle">{t('signin.form.subtitle')}</p>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email">{t('signin.form.emailLabel')}</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder={t('signin.form.emailPlaceholder')}
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">{t('signin.form.passwordLabel')}</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  placeholder={t('signin.form.passwordPlaceholder')}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              {error && (
                <div style={{
                  color: '#ff6b6b',
                  backgroundColor: 'rgba(255, 107, 107, 0.1)',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  fontSize: '0.9rem'
                }}>
                  {error}
                </div>
              )}
              
              {resetMessage && (
                <div style={{
                  color: resetMessage.includes('sent') ? '#51cf66' : '#ff6b6b',
                  backgroundColor: resetMessage.includes('sent') 
                    ? 'rgba(81, 207, 102, 0.1)' 
                    : 'rgba(255, 107, 107, 0.1)',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  fontSize: '0.9rem'
                }}>
                  {resetMessage}
                </div>
              )}

              <div className="form-actions">
                <label>
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  {t('signin.form.rememberMe')}
                </label>
                <a 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    setShowForgotPassword(!showForgotPassword);
                  }}
                  style={{ color: 'var(--accent)', textDecoration: 'none' }}
                >
                  {t('signin.form.forgotPassword')}
                </a>
              </div>

              {showForgotPassword && (
                <div style={{
                  padding: '16px',
                  backgroundColor: 'rgba(73, 169, 255, 0.1)',
                  borderRadius: '8px',
                  marginTop: '16px',
                  marginBottom: '16px'
                }}>
                  <p style={{ marginBottom: '12px', fontSize: '0.9rem' }}>
                    Enter your email address and we'll send you a link to reset your password.
                  </p>
                  <input
                    type="email"
                    placeholder="Email address"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      marginBottom: '12px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      backgroundColor: 'rgba(0, 0, 0, 0.3)',
                      color: '#f0f0f0'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      disabled={isResettingPassword}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: isResettingPassword ? 'rgba(73, 169, 255, 0.6)' : 'var(--accent)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: isResettingPassword ? 'not-allowed' : 'pointer',
                        opacity: isResettingPassword ? 0.7 : 1
                      }}
                    >
                      {isResettingPassword ? 'Sending...' : 'Send Reset Email'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setResetEmail('');
                        setResetMessage('');
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: 'transparent',
                        color: '#f0f0f0',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <div style={{ marginTop: '32px' }}>
                <button 
                  type="submit" 
                  className="primary-button"
                  disabled={isLoading}
                  style={{ 
                    opacity: isLoading ? 0.6 : 1, 
                    cursor: isLoading ? 'not-allowed' : 'pointer' 
                  }}
                >
                  {isLoading ? 'Signing in...' : t('signin.form.submitButton')}
                </button>
              </div>
            </form>
            <p className="auth-meta">
              {t('signin.form.meta.prefix')}{' '}
              <Link to="/signup">
                {t('signin.form.meta.link')}
              </Link>
            </p>
          </div>
        </article>
      </section>

      {/* Footer */}
      <footer className="signin-footer">
        &copy; {currentYear} {t('signin.footer.copyright')}
        <br />
        {t('signin.footer.helpPrefix')}{' '}
        <a href="mailto:contact@hedgit.ai">contact@hedgit.ai</a>
      </footer>
    </div>
  );
};

export default SignIn;

